import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { reviews } from '../data/mock';

const ReviewsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (isAutoPlaying) {
      const timer = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % reviews.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [isAutoPlaying]);

  const nextReview = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % reviews.length);
  };

  const prevReview = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  return (
    <section id="reviews" className="py-20 bg-black relative overflow-hidden">
      {/* Top border */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 animate-fadeInUp">
            What Our
            <span className="text-gold-gradient"> Customers Say</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto animate-fadeInUp animation-delay-100">
            Don't just take our word for it - hear from our satisfied customers
          </p>
        </div>

        {/* Reviews Carousel */}
        <div className="relative max-w-4xl mx-auto">
          {/* Navigation Arrows */}
          <button
            onClick={prevReview}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 lg:-translate-x-12 z-10 w-12 h-12 bg-black/80 hover:bg-amber-500/20 border border-gray-800 hover:border-amber-500/50 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 group"
          >
            <ChevronLeft className="w-5 h-5 text-gray-400 group-hover:text-amber-400" />
          </button>
          <button
            onClick={nextReview}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 lg:translate-x-12 z-10 w-12 h-12 bg-black/80 hover:bg-amber-500/20 border border-gray-800 hover:border-amber-500/50 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 group"
          >
            <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-amber-400" />
          </button>

          {/* Review Card */}
          <div className="card-gold rounded-3xl p-8 sm:p-12 relative overflow-hidden">
            {/* Quote Icon */}
            <div className="absolute top-8 right-8 opacity-10">
              <Quote className="w-24 h-24 text-amber-500" />
            </div>

            {/* Review Content */}
            <div className="relative z-10">
              {reviews.map((review, index) => (
                <div
                  key={review.id}
                  className={`transition-all duration-500 ${
                    index === currentIndex
                      ? 'opacity-100 translate-x-0'
                      : 'opacity-0 absolute inset-0 translate-x-8 pointer-events-none'
                  }`}
                >
                  {index === currentIndex && (
                    <>
                      {/* Stars */}
                      <div className="flex gap-1 mb-6">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star 
                            key={i} 
                            className="w-6 h-6 text-amber-400 fill-amber-400 animate-scaleIn" 
                            style={{ animationDelay: `${i * 100}ms` }}
                          />
                        ))}
                      </div>

                      {/* Review Text */}
                      <p className="text-xl sm:text-2xl text-white leading-relaxed mb-8 italic animate-fadeInUp">
                        "{review.text}"
                      </p>

                      {/* Reviewer Info */}
                      <div className="flex items-center gap-4 animate-fadeInUp animation-delay-200">
                        <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center shadow-[0_0_20px_rgba(212,160,23,0.4)]">
                          <span className="text-black font-bold text-lg">
                            {review.name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <h4 className="text-white font-semibold">{review.name}</h4>
                          <p className="text-gray-500 text-sm">{review.date}</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Indicators */}
          <div className="flex justify-center gap-2 mt-8">
            {reviews.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setIsAutoPlaying(false);
                  setCurrentIndex(index);
                }}
                className={`h-2 rounded-full transition-all duration-500 ${
                  index === currentIndex
                    ? 'bg-gradient-to-r from-amber-400 to-amber-600 w-6 shadow-[0_0_10px_rgba(212,160,23,0.5)]'
                    : 'bg-gray-700 w-2 hover:bg-gray-600'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
